import React from 'react';
import type { ReactNode } from 'react';

import Area from '@/components/apps/layouts/shared/Area';
import CtaActionButton from '@/components/apps/ui/cta/CtaActionButton';
import SectionTitle from '@/components/apps/ui/title/SectionTitle';

import type { SolutionContentType } from './solutionAnalytics';
import {
  SECTION_TITLE_COLOR_BY_TONE,
  SURFACE_BACKGROUND_CLASS_BY_TONE,
  type SolutionSurfaceTone,
} from './solutionSurfaceTone';

/**
 * ### SolutionCtaAction
 *
 * One call-to-action button: where it leads, what it says, and how the click
 * is reported.
 */
export type SolutionCtaAction = {
  /**
   * ### href
   *
   * Destination, site-relative; the link is localised for the current page.
   */
  href: string;

  /**
   * ### label
   *
   * Button text, already translated.
   */
  label: ReactNode;

  /**
   * ### analyticsContentId
   *
   * Reported as `item_id` on `select_content`. Also keys the button among its
   * siblings, so it is unique within the section.
   */
  analyticsContentId: string;

  /**
   * ### analyticsContentType
   *
   * Reported as `content_type` on `select_content`.
   */
  analyticsContentType: SolutionContentType;

  /**
   * ### variant
   *
   * Button emphasis. A section with several actions gives the primary one the
   * fill and the rest the outline.
   *
   * @defaultValue `'fill'` — the filled button
   */
  variant?: 'fill' | 'line';

  /**
   * ### target
   *
   * Browsing context the destination opens in. Set to `'_blank'` for a
   * destination outside the site, so the reader keeps this page.
   */
  target?: '_blank';

  /**
   * ### className
   *
   * Optional surface-specific classes for a button that needs a stronger
   * contrast treatment than the shared tone provides.
   */
  className?: string;
};

/**
 * ### SolutionCtaSectionProps
 *
 * Copy, actions, analytics identity, and surface tone of a call-to-action
 * section. At least one action is required; the section has no purpose
 * without one.
 */
export type SolutionCtaSectionProps = {
  /**
   * ### title
   *
   * Section heading, already translated.
   */
  title: ReactNode;

  /**
   * ### description
   *
   * One-line lead under the heading. Omitting it removes the lead element
   * entirely rather than rendering an empty one.
   */
  description?: ReactNode;

  /**
   * ### actions
   *
   * Buttons in display order. A single action sits directly under the title;
   * two or more share a wrapping row.
   */
  actions: readonly [SolutionCtaAction, ...Array<SolutionCtaAction>];

  /**
   * ### analyticsSectionId
   *
   * Value of `data-analytics-section` on the section and of
   * `data-analytics-section-id` on every button, so the view and the click
   * report the same `section_id`.
   */
  analyticsSectionId: string;

  /**
   * ### tone
   *
   * Surface tone that decides background, text, and button colors.
   *
   * @defaultValue `'dark'` — the solution-page surface
   */
  tone?: SolutionSurfaceTone;
};

/**
 * ### SolutionCtaSection
 *
 * Renders a closing call-to-action: a section title followed by one or more
 * link buttons that carry the click-tracking attributes the landing analytics
 * hook reads. Shared by the solution and home pages; each supplies its own
 * copy, destinations, and surface tone.
 *
 * @param props Copy, actions, analytics identity, and surface tone
 * @returns The rendered call-to-action section element
 * @see {@link SolutionCtaSectionProps} Defaults applied to omitted props
 */
export function SolutionCtaSection({
  title,
  description,
  actions,
  analyticsSectionId,
  tone = 'dark',
}: SolutionCtaSectionProps) {
  const [firstAction, ...restActions] = actions;

  return (
    <section
      className={`${SURFACE_BACKGROUND_CLASS_BY_TONE[tone]} pt-[clamp(80px,8.333vw,120px)]`}
      data-analytics-section={analyticsSectionId}
    >
      <Area>
        <div className="flex flex-col items-start gap-8 lg:gap-10">
          <SectionTitle
            color={SECTION_TITLE_COLOR_BY_TONE[tone]}
            title={title}
            description={description}
          />
          {restActions.length === 0 ? (
            <SolutionCtaActionButton
              action={firstAction}
              tone={tone}
              analyticsSectionId={analyticsSectionId}
            />
          ) : (
            <div className="flex flex-wrap items-center gap-4 lg:gap-5">
              {actions.map((action) => (
                <SolutionCtaActionButton
                  key={action.analyticsContentId}
                  action={action}
                  tone={tone}
                  analyticsSectionId={analyticsSectionId}
                />
              ))}
            </div>
          )}
        </div>
      </Area>
    </section>
  );
}

type SolutionCtaActionButtonProps = {
  action: SolutionCtaAction;
  tone: SolutionSurfaceTone;
  analyticsSectionId: string;
};

/**
 * ### SolutionCtaActionButton
 *
 * Renders one action as the shared call-to-action button, carrying the
 * click-tracking attributes. The section id is repeated on the button because
 * the analytics hook reads it from the clicked element, not from the enclosing
 * section.
 *
 * @param props Action, surface tone, and the section id to report
 * @returns The rendered link button element
 */
function SolutionCtaActionButton({
  action,
  tone,
  analyticsSectionId,
}: SolutionCtaActionButtonProps) {
  return (
    <CtaActionButton
      href={action.href}
      variant={action.variant ?? 'fill'}
      target={action.target}
      tone={tone}
      className={action.className}
      data-analytics-content-id={action.analyticsContentId}
      data-analytics-content-type={action.analyticsContentType}
      data-analytics-section-id={analyticsSectionId}
    >
      {action.label}
    </CtaActionButton>
  );
}
